import { useQuery } from '@tanstack/react-query';

import { getOrCreateProfile } from '@api-service/profile';
import type { UserProfile } from '@api-service/profile';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { withAccessTokenLock } from '@lib/auth0/access-token-lock';
import { ACCESS_TOKEN_ROUTE } from '@lib/auth0/access-token-route';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

export const USERS_ME_QUERY_KEY = ['users-me'] as const;

/**
 * Rotates the session for a fresh access token, or null if unavailable. Used
 * after creating a profile so the token carries the `profileId` claim, which
 * ffh-backend can only stamp once the profile exists. See
 * pages/api/auth/access-token.ts.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const res = await withAccessTokenLock(() =>
      fetch(ACCESS_TOKEN_ROUTE, { method: 'POST' })
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.token ?? null;
  } catch (error) {
    console.error(
      'Failed to refresh access token before profile create:',
      error
    );
    return null;
  }
};

/**
 * Fetches the logged-in user's ffh-backend profile, creating it on first use.
 *
 * Mirrors scoreline-app's `useProfile` pattern: `GET /users/me` runs first and,
 * on a 404 (brand-new signup), the profile is created via `POST /users/me` with
 * the email from the Auth0 session. Returns `null` for `profile` until the
 * access token is available and the request resolves.
 */
export const useProfile = (): {
  profile: UserProfile | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} => {
  const { accessToken } = useAccessToken();
  const { user } = useAuth0User();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...USERS_ME_QUERY_KEY, accessToken],
    queryFn: () =>
      getOrCreateProfile(
        accessToken as string,
        {
          email: user?.email
        },
        refreshAccessToken
      ),
    enabled: !!accessToken && !!user,
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });

  return {
    profile: data ?? null,
    isLoading: isLoading && !!accessToken && !!user,
    isError: isError && !!accessToken && !!user,
    refetch
  };
};
